import { ChevronRight, Home } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { cn } from '@/utils/cn'

export interface Crumb {
  label: string
  to?: string
}

export function Breadcrumbs({ items, homeHref }: { items: Crumb[]; homeHref?: string }) {
  const { t } = useTranslation()
  return (
    <nav aria-label={t('Breadcrumb')} className="mb-2">
      <ol className="flex flex-wrap items-center gap-1 text-xs text-muted">
        {homeHref ? (
          <li className="flex items-center gap-1">
            <Link to={homeHref} className="rounded-xs p-0.5 hover:text-brand-600" aria-label={t('Home')}>
              <Home className="size-3.5" />
            </Link>
            <ChevronRight className="size-3 text-subtle" aria-hidden="true" />
          </li>
        ) : null}
        {items.map((item, i) => {
          const last = i === items.length - 1
          return (
            <li key={`${item.label}-${i}`} className="flex items-center gap-1">
              {item.to && !last ? (
                <Link to={item.to} className="rounded-xs hover:text-brand-600">
                  {item.label}
                </Link>
              ) : (
                <span
                  aria-current={last ? 'page' : undefined}
                  className={cn(last && 'font-medium text-ink-soft')}
                >
                  {item.label}
                </span>
              )}
              {!last ? <ChevronRight className="size-3 text-subtle" aria-hidden="true" /> : null}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

interface PageHeaderProps {
  title: ReactNode
  description?: ReactNode
  breadcrumbs?: Crumb[]
  homeHref?: string
  actions?: ReactNode
  meta?: ReactNode
  className?: string
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  homeHref,
  actions,
  meta,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between', className)}>
      <div className="min-w-0">
        {breadcrumbs?.length ? <Breadcrumbs items={breadcrumbs} homeHref={homeHref} /> : null}
        <h1 className="text-2xl font-semibold text-balance sm:text-[1.625rem]">{title}</h1>
        {description ? <p className="mt-1 max-w-2xl text-sm text-muted">{description}</p> : null}
        {meta ? <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2 md:justify-end">{actions}</div> : null}
    </header>
  )
}
